/**
 * S3 multipart upload via AppSync: initialize → getPresignedUrl → PUT parts → finalize.
 */

import {
  initializeFileUpload,
  getPresignedUploadUrls,
  finalizeFileUpload,
} from "layouts/dashboardLayout/pages/Users/services/graphqlService";

/** S3 minimum part size (except last part). */
export const S3_MIN_PART_BYTES = 5 * 1024 * 1024;

const DEFAULT_PART_BYTES = 8 * 1024 * 1024;
const MAX_PART_COUNT = 10_000;
const MAX_CONCURRENT_UPLOADS = 4;
const UPLOAD_NAME_CHARS =
  "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

function extractSafeExtension(originalName) {
  const base = String(originalName || "").trim();
  const match = base.match(/(\.[a-zA-Z0-9]{1,10})$/);
  return match ? match[1] : "";
}

/** Random alphanumeric upload name (no spaces); keeps a safe file extension when present. */
export function generateSafeUploadFileName(originalName) {
  const ext = extractSafeExtension(originalName);
  let random = "";

  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    random = Array.from(
      bytes,
      (b) => UPLOAD_NAME_CHARS[b % UPLOAD_NAME_CHARS.length],
    ).join("");
  } else {
    random = Array.from(
      { length: 24 },
      () => UPLOAD_NAME_CHARS[Math.floor(Math.random() * UPLOAD_NAME_CHARS.length)],
    ).join("");
  }

  return `${random}${ext}`;
}

/**
 * @param {number} fileSize
 * @returns {{ partSize: number, parts: number }}
 */
export function calculateUploadParts(fileSize) {
  if (!fileSize || fileSize <= 0) {
    return { partSize: 0, parts: 0 };
  }
  if (fileSize <= S3_MIN_PART_BYTES) {
    return { partSize: fileSize, parts: 1 };
  }
  let partSize = DEFAULT_PART_BYTES;
  let parts = Math.ceil(fileSize / partSize);
  if (parts > MAX_PART_COUNT) {
    parts = MAX_PART_COUNT;
    partSize = Math.ceil(fileSize / parts);
  }
  return { partSize, parts };
}

function parseMessagePayload(message) {
  if (message == null || message === "") return null;
  if (typeof message === "object") return message;
  try {
    return JSON.parse(message);
  } catch {
    return { raw: message };
  }
}

function extractInitializeSession(fileResponse) {
  const parsed = parseMessagePayload(fileResponse?.Message);
  if (!parsed || typeof parsed !== "object") {
    throw new Error(
      fileResponse?.Message || "Failed to start file upload (empty response)",
    );
  }
  const fileId =
    parsed.fileId ??
    parsed.FileId ??
    parsed.uploadId ??
    parsed.UploadId ??
    parsed.id;
  const fileKey =
    parsed.fileKey ??
    parsed.FileKey ??
    parsed.key ??
    parsed.Key ??
    parsed.fileName;
  const bucket = parsed.bucket ?? parsed.Bucket;
  if (!fileId || !fileKey) {
    throw new Error(
      parsed.message ||
        parsed.error ||
        "Upload initialize did not return fileId and fileKey",
    );
  }
  return {
    fileId: String(fileId),
    fileKey: String(fileKey),
    bucket: bucket != null ? String(bucket) : "",
  };
}

function extractPresignedUrlList(fileResponse, expectedParts) {
  const parsed = parseMessagePayload(fileResponse?.Message);
  if (!parsed) {
    throw new Error("Failed to get presigned URLs (empty response)");
  }

  let rawList = null;
  if (Array.isArray(parsed)) {
    rawList = parsed;
  } else if (Array.isArray(parsed.urls)) {
    rawList = parsed.urls;
  } else if (Array.isArray(parsed.presignedUrls)) {
    rawList = parsed.presignedUrls;
  } else if (Array.isArray(parsed.signedUrls)) {
    rawList = parsed.signedUrls;
  } else if (Array.isArray(parsed.parts)) {
    rawList = parsed.parts;
  } else if (typeof parsed === "object") {
    const keys = Object.keys(parsed).filter((k) => /^\d+$/.test(k));
    if (keys.length > 0) {
      rawList = keys
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => parsed[k]);
    }
  }

  if (!rawList?.length) {
    throw new Error(
      parsed.message ||
        parsed.error ||
        "Presigned URL response did not include upload URLs",
    );
  }

  const normalized = rawList.map((entry, index) => {
    if (typeof entry === "string") {
      return { partNumber: index + 1, url: entry };
    }
    const partNumber =
      entry.partNumber ??
      entry.PartNumber ??
      entry.part ??
      entry.index ??
      index + 1;
    const url =
      entry.url ??
      entry.presignedUrl ??
      entry.signedUrl ??
      entry.uploadUrl ??
      entry.href;
    if (!url) {
      throw new Error(`Missing URL for upload part ${partNumber}`);
    }
    return { partNumber: Number(partNumber), url: String(url) };
  });

  normalized.sort((a, b) => a.partNumber - b.partNumber);
  if (expectedParts && normalized.length !== expectedParts) {
    console.warn(
      `Expected ${expectedParts} presigned URLs, received ${normalized.length}`,
    );
  }
  return normalized;
}

function extractFinalizeResult(fileResponse) {
  const parsed =
    fileResponse == null
      ? null
      : typeof fileResponse === "string"
      ? parseMessagePayload(fileResponse)
      : parseMessagePayload(fileResponse?.Message) ?? fileResponse;

  if (!parsed || typeof parsed !== "object") {
    return {};
  }
  return {
    bucket: parsed.bucket ?? parsed.Bucket ?? "",
    fileName:
      parsed.fileName ??
      parsed.FileName ??
      parsed.fileKey ??
      parsed.FileKey ??
      parsed.key ??
      parsed.Key ??
      "",
    fileId: parsed.fileId ?? parsed.FileId ?? parsed.uploadId ?? "",
  };
}

async function uploadPart(url, blob) {
  const response = await fetch(url, {
    method: "PUT",
    body: blob,
  });
  if (!response.ok) {
    throw new Error(`Part upload failed (HTTP ${response.status})`);
  }
  const etag = response.headers.get("ETag")?.replace(/"/g, "");
  if (!etag) {
    throw new Error("Part upload succeeded but ETag header was missing");
  }
  return etag;
}

async function uploadPartsWithConcurrency(
  file,
  partSize,
  presignedParts,
  onProgress,
) {
  const completedParts = [];
  let uploadedBytes = 0;
  const totalBytes = file.size;

  const uploadSinglePart = async (entry, index) => {
    const partNumber = entry.partNumber ?? index + 1;
    const isLast = index === presignedParts.length - 1;
    const start = (partNumber - 1) * partSize;
    const end = isLast ? file.size : Math.min(start + partSize, file.size);
    const blob = file.slice(start, end);
    const etag = await uploadPart(entry.url, blob);
    uploadedBytes += blob.size;
    if (onProgress) {
      onProgress(Math.min(100, Math.round((uploadedBytes / totalBytes) * 100)));
    }
    return { PartNumber: partNumber, ETag: etag };
  };

  const queue = presignedParts.map((part, index) => ({ part, index }));
  const workers = Array.from(
    { length: Math.min(MAX_CONCURRENT_UPLOADS, queue.length) },
    async () => {
      while (queue.length > 0) {
        const item = queue.shift();
        if (!item) break;
        const result = await uploadSinglePart(item.part, item.index);
        completedParts.push(result);
      }
    },
  );
  await Promise.all(workers);

  completedParts.sort((a, b) => a.PartNumber - b.PartNumber);
  return completedParts;
}

/**
 * Upload one file using multipart presigned flow.
 *
 * @param {File} file
 * @param {string} idToken
 * @param {(percent: number) => void} [onProgress] 0–100
 * @returns {Promise<{ fileName: string, fileKey: string, fileId: string, originalName: string, bucket?: string }>}
 */
export async function uploadFile(file, idToken, onProgress) {
  if (!file) throw new Error("No file selected");
  if (!idToken) {
    throw new Error("Authentication is required to upload documents");
  }

  const { partSize, parts } = calculateUploadParts(file.size);
  if (!parts) throw new Error("File is empty");

  if (onProgress) onProgress(0);

  const originalName = String(file.name || "file").trim() || "file";
  const safeFileName = generateSafeUploadFileName(originalName);
  const initRes = await initializeFileUpload(safeFileName, idToken);
  const session = extractInitializeSession(initRes);

  const urlRes = await getPresignedUploadUrls(
    session.fileId,
    session.fileKey,
    parts,
    idToken,
  );
  const presignedParts = extractPresignedUrlList(urlRes, parts);

  const uploadedParts = await uploadPartsWithConcurrency(
    file,
    partSize,
    presignedParts,
    onProgress,
  );

  const finalizeRes = await finalizeFileUpload(
    {
      fileId: session.fileId,
      fileKey: session.fileKey,
      parts: uploadedParts,
    },
    idToken,
  );

  const finalized = extractFinalizeResult(finalizeRes);
  const fileName =
    finalized.fileName ||
    finalized.fileKey ||
    session.fileKey ||
    safeFileName;
  const bucket = finalized.bucket || session.bucket || "";

  if (!fileName) {
    throw new Error(
      "Upload completed but file name was not returned. Check finalize response.",
    );
  }

  if (onProgress) onProgress(100);

  return {
    fileName: String(fileName),
    fileKey: session.fileKey,
    fileId: session.fileId,
    originalName,
    ...(bucket ? { bucket: String(bucket) } : {}),
  };
}

/** @deprecated Use uploadFile */
export const uploadMissionDocument = uploadFile;
